/**
 * The models Prop Studio has placed for each prop, for the designer: each
 * variant with the size it is staged at and the preview to draw in the
 * stand-in's place — read from Prop Studio's back end (the site's
 * api/props.php in a built app, the Node helper under `npm start`), and again
 * whenever the tab comes back into focus, so a placement saved in Prop Studio
 * is picked up for the next Stage. The library, the models' previews and the
 * texture maps are public reads (props.php `$publicRead`, since 2026-10-29):
 * a customer's view shows the models and the space's render look, not only an
 * admin's. Without a library at all (a site never synced, the helper not
 * running), undefined: the kit's sizes and the procedural props apply.
 *
 * Shared by the sandbox page and the Advanced mode page.
 */

import { useEffect, useState } from 'react';
import { LIBRARY_CHANGED_EVENT, getLibrary } from '../props/studioApi';
import type { Catalogue } from '../../calculations/staging/catalogue';
import { PropVariant, baseId, propVariants } from '../props/variants';
import type { FinishPools } from '../ui/room3d/renderFinish';
import type { LookPools } from '../ui/room3d/renderLooks';

export type PropVariants = Record<string, PropVariant[]>;

export function usePropLibrary(): { variants: PropVariants | undefined; catalogue: Catalogue | undefined; finishes: FinishPools | undefined; looks: LookPools | undefined } {
  const [variants, setVariants] = useState<PropVariants | undefined>(undefined);
  const [catalogue, setCatalogue] = useState<Catalogue | undefined>(undefined);
  const [finishes, setFinishes] = useState<FinishPools | undefined>(undefined);
  const [looks, setLooks] = useState<LookPools | undefined>(undefined);
  useEffect(() => {
    let stale = false;
    const read = async () => {
      try {
        const lib = await getLibrary();
        if (stale) return;
        // Each prop's placed models: staging picks one per copy and stages
        // it at that model's own size (stage.ts).
        const next: PropVariants = {};
        for (const id of new Set(Object.keys(lib.props).map(baseId))) {
          const list = propVariants(lib, id);
          if (list.length) next[id] = list;
        }
        // The same object while nothing changed: the host memoises on its identity.
        setVariants((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
        // Where each prop can go and which spaces it is in (Prop Studio's catalogue), the same way.
        const cat = lib.catalogue ?? {};
        setCatalogue((prev) => (prev && JSON.stringify(prev) === JSON.stringify(cat) ? prev : cat));
        // And the finishes by space Stage draws the render's floor set and HDRI from.
        const fin = lib.finishes ?? {};
        setFinishes((prev) => (prev && JSON.stringify(prev) === JSON.stringify(fin) ? prev : fin));
        // And the render looks by space (the Look Lab's): the 3D view shows the one the room's render gets.
        const lk = lib.looks ?? {};
        setLooks((prev) => (prev && JSON.stringify(prev) === JSON.stringify(lk) ? prev : lk));
      } catch {
        if (!stale) { setVariants(undefined); setCatalogue(undefined); setFinishes(undefined); setLooks(undefined); } // no helper: the kits as they are
      }
    };
    read();
    window.addEventListener('focus', read);
    window.addEventListener(LIBRARY_CHANGED_EVENT, read);
    return () => { stale = true; window.removeEventListener('focus', read); window.removeEventListener(LIBRARY_CHANGED_EVENT, read); };
  }, []);
  return { variants, catalogue, finishes, looks };
}
